import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isAbortedRequest, isTimedOutRequest, useRpcCaller } from "../context/WidgetRequestContext";
import type { CustomerPreOrder, CustomerPreOrderItemUpdateRequest } from "../../../common/jsonRpcTypes";
import type { RpcOutput } from "../../../common/jsonRpcRoutes";
import {
  alignDietaryRequirementNames,
  hasMoreNamesThanGuests,
  hasReachedNameLimit,
  hasStaleDietaryRequirementNames,
  pruneDietaryRequirementAt,
} from "../preOrders/preOrderUtils";

/**
 * Cost fields carried by the `/removeNameItems` response, recalculated once the
 * removed person's items have been dropped from the basket.
 *
 * The tax fields are part of this set because `PricingSummary` renders the Total
 * from `total_gross` on a tax-exclusive venue, so refreshing `totalCost` alone
 * would leave the figure on screen unchanged.
 */
interface RemoveNameItemsTotals {
  totalCost?: number;
  totalCostWithService?: number;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  requiredPaymentAmount?: number;
  tax_inclusive?: boolean;
  tax_label?: string;
  tax_breakdown?: { label: string; amount: number }[];
  total_net?: number;
  total_gross?: number;
}

const REMOVAL_TOTAL_FIELDS = [
  "totalCost",
  "totalCostWithService",
  "serviceChargeAmount",
  "serviceChargePercentage",
  "requiredPaymentAmount",
  "tax_inclusive",
  "tax_label",
  "tax_breakdown",
  "total_net",
  "total_gross",
] as const satisfies readonly (keyof RemoveNameItemsTotals)[];

/**
 * Picks the basket a name update acted on, the way the server resolves it: the
 * requested booking type, otherwise the session's only basket, otherwise the
 * default one.
 */
function selectNamedBasket(baskets: RpcOutput<"/getAllBookingTypeBaskets">, bookingType: string | undefined) {
  const requested = bookingType ? baskets.find((basket) => basket.bookingType === bookingType) : undefined;
  if (requested) return requested;
  return baskets.length === 1 ? baskets[0] : baskets.find((basket) => basket.bookingType === "default");
}

interface UseMultiPersonOrderingParams {
  bookingId: string;
  bookingType?: string;
  customerPreOrder: CustomerPreOrder | undefined;
  setCustomerPreOrder: React.Dispatch<React.SetStateAction<CustomerPreOrder | undefined>>;
  updateCustomerPreOrder: (request: CustomerPreOrderItemUpdateRequest) => Promise<boolean>;
  refreshCustomerPreOrder: () => Promise<void>;
  /** Re-merges the basket from the server after a timed-out mutation. */
  reloadServerBasket?: () => Promise<void>;
  forNames: string[];
  setForNames: React.Dispatch<React.SetStateAction<string[]>>;
  multipleNames: boolean;
  setMultipleNames: React.Dispatch<React.SetStateAction<boolean>>;
  editNames: boolean;
  setEditNames: React.Dispatch<React.SetStateAction<boolean>>;
  /** Party size of the booking, capping how many names may be added. */
  guestCount?: number;
}

interface UseMultiPersonOrderingReturn {
  orderingFor: number;
  addName: string;
  setAddName: React.Dispatch<React.SetStateAction<string>>;
  addNameError: string;
  /** True when the booking holds more names than guests, e.g. after the party size was lowered. */
  namesExceedGuests: boolean;
  handleMultipleNamesOptionChange: (newValue?: boolean) => void;
  /** Resolves to whether the names were saved. */
  syncNamesToRedis: (newForNames: string[]) => Promise<boolean>;
  /** Renames the stored dietary requirements after the guests at the same position. */
  syncDietaryRequirementNames: (newForNames: string[]) => Promise<void>;
  handleAddName: () => Promise<void>;
  handleRemoveName: (index: number) => Promise<void>;
  handleSetOrderingFor: (index: number) => void;
}

export function useMultiPersonOrdering({
  bookingId,
  bookingType,
  customerPreOrder,
  setCustomerPreOrder,
  updateCustomerPreOrder,
  refreshCustomerPreOrder,
  reloadServerBasket,
  forNames,
  setForNames,
  multipleNames,
  setMultipleNames,
  editNames,
  setEditNames,
  guestCount,
}: UseMultiPersonOrderingParams): UseMultiPersonOrderingReturn {
  const call = useRpcCaller();
  const { t } = useTranslation();
  const [orderingFor, setOrderingFor] = useState<number>(0);
  const [addName, setAddName] = useState("");
  const [addNameError, setAddNameError] = useState("");

  const removeNameItems = useCallback(
    async (nameIndex: number, newForNames: string[]) => {
      return call("/removeNameItems", {
        booking_id: bookingId,
        pendingPreorderId: customerPreOrder?.pendingPreorderId ?? null,
        nameIndex,
        forNames: newForNames,
        booking_type: bookingType,
      });
    },
    [call, bookingId, customerPreOrder?.pendingPreorderId, bookingType],
  );

  const syncNamesToRedis = useCallback(
    async (newForNames: string[]): Promise<boolean> => {
      if (!customerPreOrder) return false;

      try {
        return await updateCustomerPreOrder({
          booking_id: bookingId,
          pendingPreorderId: customerPreOrder.pendingPreorderId ?? null,
          itemId: "",
          menuId: "",
          amount: 0,
          multipleNames: newForNames.length > 0,
          forNames: newForNames,
          orderingFor: Math.min(orderingFor, Math.max(0, newForNames.length - 1)),
          booking_type: bookingType,
        });
      } catch (error) {
        if (!isAbortedRequest(error)) console.error("Error syncing names to Redis:", error);
        return false;
      }
    },
    [bookingId, bookingType, customerPreOrder, orderingFor, updateCustomerPreOrder],
  );

  const handleMultipleNamesOptionChange = useCallback(
    (newValue?: boolean) => {
      setAddNameError("");
      if (newValue !== undefined) {
        setMultipleNames(newValue);
      }

      if (newValue && (!forNames || forNames.length === 0)) {
        setForNames([]);
        setEditNames(true);
      } else if (!newValue) {
        setForNames([]);
        setEditNames(false);
      }
    },
    [forNames, setMultipleNames, setForNames, setEditNames],
  );

  // Read after the name sync has awaited, so a list the guest saved in the
  // dietary modal meanwhile is renamed and sent rather than the earlier one.
  const latestDietaryRequirements = useRef(customerPreOrder?.dietaryRequirements);
  useEffect(() => {
    latestDietaryRequirements.current = customerPreOrder?.dietaryRequirements;
  }, [customerPreOrder?.dietaryRequirements]);

  /**
   * Names the dietary requirements after the guests at the same position.
   *
   * A requirement's name is captured when the dietary modal is saved, so one
   * saved before its guest's name was added — or before the name was edited —
   * would reach Collins unnamed or under the old name. Like the removal below,
   * this must run after the name sync: the requirements live alongside the
   * basket under one session key that every basket write replaces wholesale.
   */
  const syncDietaryRequirementNames = useCallback(
    async (newForNames: string[]) => {
      const existing = latestDietaryRequirements.current;
      if (!existing || !hasStaleDietaryRequirementNames(existing, newForNames)) return;

      const dietaryRequirements = alignDietaryRequirementNames(existing, newForNames);
      setCustomerPreOrder((prev) => (prev ? { ...prev, dietaryRequirements } : prev));

      try {
        await call("/updateDietaryRequirements", {
          booking_id: bookingId,
          dietaryRequirements,
          ...(customerPreOrder?.pending ? { pending: true } : {}),
        });
      } catch (error) {
        if (isAbortedRequest(error)) return;
        console.error("Error syncing dietary requirement names:", error);
      }
    },
    [call, bookingId, customerPreOrder?.pending, setCustomerPreOrder],
  );

  /**
   * The names the server basket holds, or undefined when they cannot be read.
   * A failed name sync may still have been saved — a timed-out request can
   * complete on the server after the client stops waiting — so these, not the
   * local list, decide what the guest sees.
   */
  const fetchServerForNames = useCallback(async (): Promise<string[] | undefined> => {
    try {
      const baskets = await call("/getAllBookingTypeBaskets", { booking_id: bookingId });
      return selectNamedBasket(baskets, bookingType)?.forNames;
    } catch (error) {
      if (!isAbortedRequest(error)) console.error("Error reading the saved names:", error);
      return undefined;
    }
  }, [call, bookingId, bookingType]);

  /** Applies the outcome of a name that was saved, renaming its dietary requirements after it. */
  const completeAddName = useCallback(
    async (savedForNames: string[], newName: string) => {
      setForNames(savedForNames);
      await syncDietaryRequirementNames(savedForNames);
      setOrderingFor(savedForNames.indexOf(newName));
    },
    [setForNames, syncDietaryRequirementNames],
  );

  const handleAddName = useCallback(async () => {
    setAddNameError("");
    if (!addName) return;
    if (forNames.includes(addName)) {
      setAddNameError(t("person_already_in_list", "That person is already in the list"));
      return;
    }
    if (hasReachedNameLimit(forNames.length, guestCount)) {
      setAddNameError(t("max_names_for_guests_reached", "You've added a name for every guest in your booking"));
      return;
    }
    const newName = addName;
    const newForNames = [...forNames, newName];
    setForNames(newForNames);
    setAddName("");

    if (await syncNamesToRedis(newForNames)) {
      await completeAddName(newForNames, newName);
      return;
    }

    const serverForNames = await fetchServerForNames();
    if (serverForNames?.includes(newName)) {
      await completeAddName(serverForNames, newName);
      return;
    }
    setForNames(serverForNames ?? ((current) => current.filter((name) => name !== newName)));
    setAddName((current) => current || newName);
    setAddNameError(t("name_save_failed", "Sorry, we couldn't save this name. Please try again."));
  }, [addName, forNames, guestCount, t, syncNamesToRedis, fetchServerForNames, completeAddName, setForNames]);

  /**
   * Drops the dietary requirements belonging to a removed person.
   *
   * Requirements are stored positionally, aligned with `forNames`, so leaving a
   * stale entry behind would shift every later person's allergies and diet
   * types onto the wrong guest — both in the menu suitability check and in the
   * order submitted to Collins.
   *
   * This must run last in the removal sequence. Locally, the basket refresh and
   * the name sync both re-apply a pre-order captured in their own render
   * closure, so an earlier prune would be reinstated; the state update therefore
   * prunes from the newest state rather than the captured snapshot. On the
   * server, the requirements live alongside the basket under one session key
   * that every basket write replaces wholesale, so the persisted list survives
   * only when it is written after the item and name updates.
   */
  const removeDietaryRequirementsForName = useCallback(
    async (index: number) => {
      const existing = customerPreOrder?.dietaryRequirements;
      if (!existing || index >= existing.length) return;

      setCustomerPreOrder((prev) => {
        const latest = prev?.dietaryRequirements;
        if (!prev || !latest || index >= latest.length) return prev;
        return { ...prev, dietaryRequirements: pruneDietaryRequirementAt(latest, index) };
      });

      try {
        await call("/updateDietaryRequirements", {
          booking_id: bookingId,
          dietaryRequirements: pruneDietaryRequirementAt(existing, index),
          ...(customerPreOrder?.pending ? { pending: true } : {}),
        });
      } catch (error) {
        if (isAbortedRequest(error)) return;
        console.error(`Error removing dietary requirements for name at index ${index}:`, error);
      }
    },
    [call, bookingId, customerPreOrder, setCustomerPreOrder],
  );

  /**
   * Applies the totals recalculated by `/removeNameItems` to the local basket.
   *
   * This must run after the basket refresh and the name sync. Both of those
   * write `{ ...customerPreOrder, ...response }` from a pre-order captured in
   * their own render closure, so they reinstate the totals held before the
   * removal — the items disappear from the basket but the Total does not move.
   * Updating from the newest state instead of the captured snapshot keeps the
   * recalculated figures in place.
   *
   * Only fields the server actually returned are applied, so a failed Collins
   * recalculation never blanks a total that is still correct.
   */
  const applyRemovalTotals = useCallback(
    (totals: RemoveNameItemsTotals) => {
      const applied: RemoveNameItemsTotals = {};
      for (const field of REMOVAL_TOTAL_FIELDS) {
        if (totals[field] !== undefined) {
          (applied as Record<string, unknown>)[field] = totals[field];
        }
      }
      if (Object.keys(applied).length === 0) return;

      setCustomerPreOrder((prev) => (prev ? { ...prev, ...applied } : prev));
    },
    [setCustomerPreOrder],
  );

  const handleRemoveName = useCallback(
    async (index: number) => {
      setAddNameError("");
      const newForNames = [...forNames];
      newForNames.splice(index, 1);
      setForNames(newForNames);
      let removalTotals: RemoveNameItemsTotals = {};
      if (customerPreOrder?.items) {
        try {
          const result = await removeNameItems(index, newForNames);
          removalTotals = result;
        } catch (error) {
          // The removal may still have been applied server-side.
          // Awaited so the refresh below cannot be overwritten by a later reload response.
          if (isTimedOutRequest(error)) await reloadServerBasket?.();
          if (!isAbortedRequest(error)) {
            console.error(`Error removing items for name at index ${index}:`, error);
          }
        } finally {
          await refreshCustomerPreOrder();
        }
      }
      await syncNamesToRedis(newForNames);
      applyRemovalTotals(removalTotals);
      await removeDietaryRequirementsForName(index);
      if (orderingFor >= newForNames.length) {
        setOrderingFor(Math.max(0, newForNames.length - 1));
      } else if (orderingFor > index) {
        setOrderingFor(orderingFor - 1);
      }
    },
    [
      customerPreOrder,
      forNames,
      syncNamesToRedis,
      removeNameItems,
      removeDietaryRequirementsForName,
      applyRemovalTotals,
      orderingFor,
      refreshCustomerPreOrder,
      reloadServerBasket,
      setForNames,
    ],
  );

  const handleSetOrderingFor = useCallback((index: number) => {
    setOrderingFor(index);
  }, []);

  return {
    orderingFor,
    addName,
    setAddName,
    addNameError,
    namesExceedGuests: hasMoreNamesThanGuests(forNames.length, guestCount),
    handleMultipleNamesOptionChange,
    syncNamesToRedis,
    syncDietaryRequirementNames,
    handleAddName,
    handleRemoveName,
    handleSetOrderingFor,
  };
}
